import type { PolyRelayPosition } from '@/lib/polymarket/relay';

/**
 * Side-specific close fences for Polymarket positions (#429).
 *
 * Key: the existing `<conditionId>-<side>` row identity. Value: the validated
 * lifetime `totalBoughtCount` captured when the trader opened the Close
 * confirmation, or `null` when old/malformed data cannot establish a baseline.
 *
 * A verified-flat close fences its row so the lagging fills ledger can't
 * re-render a stale copy with a live Close button (a double-sell invitation).
 * The fence releases only on ledger proof: the row goes flat/absent in an
 * authoritative snapshot, or the lifetime buy count strictly advances past the
 * captured baseline — evidence that a later buy fill created a new position
 * generation. Nothing else (fill timestamps, sold counts, net quantity,
 * prices, timers) is re-buy proof; see the #429 design doc.
 */
export type ClosedPolyPositionFences = ReadonlyMap<string, number | null>;

export function polyPositionKey(
  position: Pick<PolyRelayPosition, 'ticker' | 'side'>,
): string {
  return `${position.ticker}-${position.side}`;
}

/** Validate a close-time lifetime buy count. Finite and > 0 only — every
 *  other shape (old cached payloads, malformed data) yields `null`, which
 *  keeps the fence fail-safe: it then releases only on flat/absent. */
export function polyBuyBaseline(totalBoughtCount: unknown): number | null {
  return typeof totalBoughtCount === 'number' &&
    Number.isFinite(totalBoughtCount) &&
    totalBoughtCount > 0
    ? totalBoughtCount
    : null;
}

/** True when the fenced row is still the stale pre-close generation. A valid
 *  current count strictly greater than a valid baseline proves a re-buy and
 *  reveals the row immediately — before the reconcile effect runs. Equality
 *  is the stale generation; a lower value is a ledger correction, not a buy. */
export function isClosedPolyPositionSuppressed(
  fences: ClosedPolyPositionFences,
  position: Pick<PolyRelayPosition, 'ticker' | 'side' | 'totalBoughtCount'>,
): boolean {
  const key = polyPositionKey(position);
  if (!fences.has(key)) return false;
  const baseline = fences.get(key) ?? null;
  if (baseline === null) return true;
  const current = polyBuyBaseline(position.totalBoughtCount);
  return current === null || current <= baseline;
}

/**
 * Drop fences whose rows the ledger has caught up on: flat/absent rows, or
 * rows whose lifetime buy count strictly advanced past the baseline.
 *
 * `isAuthoritative` must be `polyPositionsState.status === 'ready'`. When
 * false the input is returned untouched — a coded-unavailable streak empties
 * `positions` without saying anything about the ledger, and releasing fences
 * on that blind read is exactly the transient-outage double-sell #429 closes.
 * Holding a fence through an outage is the safe direction; recovery is one
 * authoritative poll away.
 *
 * Referentially stable: returns the input map when nothing changed so the
 * reconcile effect can skip its state write on no-op polls.
 */
export function reconcileClosedPolyPositionFences(
  fences: ClosedPolyPositionFences,
  positions: ReadonlyArray<
    Pick<PolyRelayPosition, 'ticker' | 'side' | 'netCount' | 'totalBoughtCount'>
  >,
  isAuthoritative: boolean,
): ClosedPolyPositionFences {
  if (fences.size === 0 || !isAuthoritative) return fences;

  const heldCounts = new Map<string, number | null>();
  for (const position of positions) {
    if (Number.isFinite(position.netCount) && position.netCount > 0) {
      heldCounts.set(polyPositionKey(position), polyBuyBaseline(position.totalBoughtCount));
    }
  }

  const released: string[] = [];
  for (const [key, baseline] of fences) {
    if (!heldCounts.has(key)) {
      released.push(key); // flat or absent — the ledger caught up
      continue;
    }
    const current = heldCounts.get(key) ?? null;
    if (baseline !== null && current !== null && current > baseline) {
      released.push(key); // lifetime buys advanced — new generation
    }
  }
  if (released.length === 0) return fences;

  const next = new Map(fences);
  for (const key of released) next.delete(key);
  return next;
}
